"use client"

import React, { useMemo, useState } from 'react'
import { Table } from '@tanstack/react-table'
import { Check, Search } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '../ui/popover'
import { cn } from '../../lib/utils'

export interface QuickFilterOption {
  column: string
  label?: string
  options?: string[] | "auto"
  multi?: boolean
  showCounts?: boolean
}

interface DataTableQuickFilterProps<TData> {
  table: Table<TData>
  filter: QuickFilterOption
  data: TData[]
}

export function DataTableQuickFilter<TData>({
  table,
  filter,
  data,
}: DataTableQuickFilterProps<TData>) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  
  // Get column - try multiple ways
  const column = React.useMemo(() => {
    // First try direct
    let col = table.getColumn(filter.column)
    
    // If not found, search all columns
    if (!col) {
      const allCols = table.getAllColumns()
      col = allCols.find(c => 
        c.id === filter.column || 
        (c.columnDef as any).accessorKey === filter.column
      )
    }
    
    return col
  }, [table, filter.column])

  // Get available options from data
  const availableOptions = useMemo(() => {
    // If manual options provided
    if (filter.options && filter.options !== "auto") {
      return filter.options
    }

    // Auto-detect from data
    const uniqueValues = new Set<string>()
    
    data.forEach((row: any) => {
      // Try direct access
      let value = row[filter.column]
      
      // If undefined, try nested paths
      if (value === undefined && filter.column.includes('.')) {
        const keys = filter.column.split('.')
        value = row
        for (const key of keys) {
          value = value?.[key]
        }
      }
      
      if (value != null && value !== '') {
        uniqueValues.add(String(value))
      }
    })

    return Array.from(uniqueValues).sort()
  }, [data, filter.column, filter.options])

  // Get current filter value
  const filterValue = column?.getFilterValue() as string | string[] | undefined
  const selectedValues = useMemo(() => {
    if (!filterValue) return new Set<string>()
    if (Array.isArray(filterValue)) return new Set(filterValue)
    return new Set([String(filterValue)])
  }, [filterValue])

  // Calculate counts
  const optionCounts = useMemo(() => {
    if (!filter.showCounts) return {}
    
    const counts: Record<string, number> = {}
    
    data.forEach((row: any) => {
      let value = row[filter.column]
      
      if (value === undefined && filter.column.includes('.')) {
        const keys = filter.column.split('.')
        value = row
        for (const key of keys) {
          value = value?.[key]
        }
      }
      
      if (value != null && value !== '') {
        const strValue = String(value)
        counts[strValue] = (counts[strValue] || 0) + 1
      }
    })
    
    return counts
  }, [data, filter.column, filter.showCounts])

  const handleSelect = (value: string) => {
    if (!column) return
    
    if (filter.multi) {
      const newValues = new Set(selectedValues)
      if (newValues.has(value)) {
        newValues.delete(value)
      } else {
        newValues.add(value)
      }
      
      if (newValues.size === 0) {
        column.setFilterValue(undefined)
      } else {
        // For multi-select, pass array of selected values
        column.setFilterValue(Array.from(newValues))
      }
    } else {
      if (selectedValues.has(value)) {
        column.setFilterValue(undefined)
      } else {
        column.setFilterValue(value)
      }
    }
  }

  const handleClear = () => {
    column?.setFilterValue(undefined)
    setSearchValue("")
  }

  const label = filter.label || filter.column

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchValue) return availableOptions
    
    return availableOptions.filter(option => 
      option.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [availableOptions, searchValue])

  // If no column found, still show the filter but disabled
  const isDisabled = !column || availableOptions.length === 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isDisabled}
          className={cn(
            "h-8 border-dashed",
            selectedValues.size > 0 && "border-solid"
          )}
        >
          {label}
          {selectedValues.size > 0 && (
            <>
              <span className="mx-2 h-4 w-px bg-border" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal"
              >
                {selectedValues.size}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start">
        <div className="p-2">
          {availableOptions.length > 0 && (
            <div className="pb-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={`Search ${label.toLowerCase()}...`}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="h-8 w-full pl-8"
                />
              </div>
            </div>
          )}
          
          {availableOptions.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              No options available
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              No results found
            </div>
          ) : (
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.has(option)
                return (
                  <button
                    key={option}
                    onClick={() => handleSelect(option)}
                    className={cn(
                      "flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent",
                      isSelected && "bg-accent"
                    )}
                  >
                    <div className="flex items-center">
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}
                      >
                        <Check className="h-4 w-4" />
                      </div>
                      <span>{option}</span>
                    </div>
                    {filter.showCounts && optionCounts[option] !== undefined && (
                      <span className="text-xs text-muted-foreground">
                        {optionCounts[option]}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
          {selectedValues.size > 0 && (
            <div className="border-t mt-2 pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-center"
                onClick={handleClear}
              >
                Clear filter
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}